// src/services/compositeService.js
// ============================================================================
// COMPOSITE PIPELINE — SHARED BY DASHBOARD (PREFETCH) AND EDITOR (FALLBACK)
// ----------------------------------------------------------------------------
// This module owns EVERYTHING related to turning (working base + frames) into
// a rendered composite PNG. Both Dashboard (prefetch) and Editor (fallback,
// exports, debug) import from here — there is exactly one implementation of
// the composite pipeline in the codebase.
//
// Exports:
//   loadImageElement(src)                — CORS-robust image loader
//   computeHomography(src, dst)          — 8-dof perspective matrix
//   buildFrameComposite(base, frames)    — returns a PNG data URL
//   prefetchProjectForEditor({...})      — Dashboard entry point
//   computeCompositeCacheKey(base, frames)
//   getCachedComposite(key) / setCachedComposite(key, url)
//
// The IndexedDB cache is keyed on (workingBase tail, frames fingerprint) so
// repeat opens within a session are instant (no composite rebuild).
// ============================================================================

import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { getStorage, ref as storageRef, getBlob } from 'firebase/storage';
import { db } from '../firebase';

// ============================================
// IMAGE LOADER — CORS-ROBUST
// ----------------------------------------------------------------------------
// Mobile Safari / PWA is far stricter about crossOrigin than desktop.
// The previous Editor implementation had two failure modes on mobile:
//   1. getBlob() succeeds but createImageBitmap(blob) throws → falls
//      through to <img crossOrigin="anonymous">, which the bucket rejects.
//   2. No further fallback → frame bitmap never materialises and the
//      composite silently skips it.
//
// Fix: after getBlob() succeeds, never fall through to the cross-origin
// <img> path. Use an object URL instead — same-origin by definition,
// therefore immune to CORS.
// ============================================
export const loadImageElement = async (src) => {
  if (typeof src !== 'string' || !src) {
    throw new Error('[loadImageElement] invalid src');
  }

  const isFirebaseStorage =
    src.includes('firebasestorage.googleapis.com') ||
    src.includes('storage.googleapis.com');

  // Data URLs bypass CORS entirely.
  if (src.startsWith('data:')) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('[loadImageElement] data URL decode failed'));
      img.src = src;
    });
  }

  const loadViaObjectUrl = async (blob) => {
    const objectUrl = URL.createObjectURL(blob);
    try {
      return await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('[loadImageElement] object URL <img> failed'));
        img.src = objectUrl;
      });
    } finally {
      try { URL.revokeObjectURL(objectUrl); } catch (e) { /* noop */ }
    }
  };

  const loadViaBitmap = async (blob) => {
    if (typeof createImageBitmap === 'function') {
      try {
        return await createImageBitmap(blob);
      } catch (err) {
        console.warn('[loadImageElement] createImageBitmap(blob) failed, using object URL:', err);
      }
    }
    return loadViaObjectUrl(blob);
  };

  // Path 1: Firebase SDK getBlob → bitmap or object URL
  if (isFirebaseStorage) {
    try {
      const storage = getStorage();
      const fileRef = storageRef(storage, src);
      const blob = await getBlob(fileRef);
      return await loadViaBitmap(blob);
    } catch (err) {
      console.warn('[loadImageElement] Firebase SDK getBlob path failed:', err);
    }
  }

  // Path 2: direct fetch with CORS → bitmap or object URL
  try {
    const response = await fetch(src, { mode: 'cors', credentials: 'omit' });
    if (!response.ok) throw new Error(`fetch status ${response.status}`);
    const blob = await response.blob();
    return await loadViaBitmap(blob);
  } catch (err) {
    console.warn('[loadImageElement] fetch(src) path failed:', err);
  }

  // Path 3: <img crossOrigin="anonymous"> (last CORS-safe resort)
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('[loadImageElement] crossOrigin <img> failed'));
      img.src = src;
    });
  } catch (err) {
    console.warn('[loadImageElement] crossOrigin <img> path failed:', err);
  }

  throw new Error(
    '[loadImageElement] all CORS-safe load paths failed for ' +
    src.slice(0, 120) +
    ' — verify Firebase Storage CORS includes this origin.'
  );
};

// ============================================
// HOMOGRAPHY
// ============================================
export const computeHomography = (src, dst) => {
  const A = [];
  const b = [];

  for (let i = 0; i < 4; i++) {
    const [x, y] = src[i];
    const [u, v] = dst[i];

    A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
    b.push(u);

    A.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
    b.push(v);
  }

  const n = 8;
  const M = A.map((row, i) => [...row, b[i]]);

  for (let col = 0; col < n; col++) {
    let maxRow = col;
    let maxVal = Math.abs(M[col][col]);
    for (let row = col + 1; row < n; row++) {
      const val = Math.abs(M[row][col]);
      if (val > maxVal) { maxVal = val; maxRow = row; }
    }
    [M[col], M[maxRow]] = [M[maxRow], M[col]];

    const pivot = M[col][col];
    if (Math.abs(pivot) < 1e-10) continue;

    for (let j = col; j <= n; j++) M[col][j] /= pivot;

    for (let row = 0; row < n; row++) {
      if (row !== col) {
        const factor = M[row][col];
        for (let j = col; j <= n; j++) M[row][j] -= factor * M[col][j];
      }
    }
  }

  const h = M.map(row => row[n]);
  return [
    [h[0], h[1], h[2]],
    [h[3], h[4], h[5]],
    [h[6], h[7], 1],
  ];
};

// ============================================
// COMPOSITE BUILDER
// ============================================
export const buildFrameComposite = async (baseImageUrl, frames) => {
  if (!baseImageUrl) return null;

  const placedFrames = (frames || [])
    .filter(f => f && f.corners && Array.isArray(f.corners) && f.corners.length === 4 && f.isPlaced !== false)
    .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));

  if (placedFrames.length === 0) return baseImageUrl;

  const baseBitmap = await loadImageElement(baseImageUrl);
  const canvasW = baseBitmap.width;
  const canvasH = baseBitmap.height;

  const canvas = document.createElement('canvas');
  canvas.width = canvasW;
  canvas.height = canvasH;
  // willReadFrequently: true hints the browser to keep this canvas in
  // CPU-addressable memory. The composite builder calls getImageData()
  // once per frame plus once for the base, so this avoids repeated GPU
  // readbacks (Chrome DevTools "Canvas2D: Multiple readback operations"
  // warning) and is measurably faster for our access pattern.
  const ctx = canvas.getContext('2d', {
    colorSpace: 'display-p3',
    willReadFrequently: true,
  });
  ctx.drawImage(baseBitmap, 0, 0);

  const failedFrames = [];

  for (const frame of placedFrames) {
    const frameSrc = frame.originalImage || frame.imageData || frame.dataUrl || frame.originalUncroppedData || frame.data;
    if (!frameSrc) {
      console.warn('[buildFrameComposite] Frame has no image source, skipping:', frame.id);
      continue;
    }

    let frameBitmap;
    try {
      frameBitmap = await loadImageElement(frameSrc);
    } catch (err) {
      console.error('[buildFrameComposite] Failed to load frame artwork:', {
        frameId: frame.id,
        src: typeof frameSrc === 'string' ? frameSrc.slice(0, 120) : frameSrc,
        error: err?.message || String(err),
      });
      failedFrames.push({ id: frame.id, error: err?.message || String(err) });
      continue;
    }

    const fw = frame.naturalWidth || frameBitmap.naturalWidth || frameBitmap.width;
    const fh = frame.naturalHeight || frameBitmap.naturalHeight || frameBitmap.height;
    if (!fw || !fh) continue;

    const dstCorners = frame.corners;
    const src = [[0, 0], [fw, 0], [fw, fh], [0, fh]];
    const dst = dstCorners.map(c => [c.x, c.y]);
    const H = computeHomography(src, dst);

    const a = H[0][0], b = H[0][1], c = H[0][2];
    const d = H[1][0], e = H[1][1], f = H[1][2];
    const g = H[2][0], h = H[2][1], i = H[2][2];
    const det = a * (e * i - f * h) - b * (d * i - f * g) + c * (d * h - e * g);
    if (Math.abs(det) < 1e-10) continue;

    const invH = [
      [(e * i - f * h) / det, (c * h - b * i) / det, (b * f - c * e) / det],
      [(f * g - d * i) / det, (a * i - c * g) / det, (c * d - a * f) / det],
      [(d * h - e * g) / det, (g * b - a * h) / det, (a * e - b * d) / det],
    ];

    const frameCanvas = document.createElement('canvas');
    frameCanvas.width = fw;
    frameCanvas.height = fh;
    // willReadFrequently: true — this canvas exists solely to be read
    // once via getImageData immediately after drawing. Keeping it on the
    // CPU avoids a GPU→CPU readback per frame.
    const fCtx = frameCanvas.getContext('2d', {
      colorSpace: 'display-p3',
      willReadFrequently: true,
    });
    fCtx.drawImage(frameBitmap, 0, 0, fw, fh);
    const fData = fCtx.getImageData(0, 0, fw, fh).data;

    const minX = Math.max(0, Math.floor(Math.min(...dstCorners.map(p => p.x))));
    const maxX = Math.min(canvasW - 1, Math.ceil(Math.max(...dstCorners.map(p => p.x))));
    const minY = Math.max(0, Math.floor(Math.min(...dstCorners.map(p => p.y))));
    const maxY = Math.min(canvasH - 1, Math.ceil(Math.max(...dstCorners.map(p => p.y))));

    const outData = ctx.getImageData(0, 0, canvasW, canvasH);
    const out = outData.data;

    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        const vec = [x, y, 1];
        const sx = invH[0][0] * vec[0] + invH[0][1] * vec[1] + invH[0][2] * vec[2];
        const sy = invH[1][0] * vec[0] + invH[1][1] * vec[1] + invH[1][2] * vec[2];
        const sw = invH[2][0] * vec[0] + invH[2][1] * vec[1] + invH[2][2] * vec[2];
        if (Math.abs(sw) < 1e-10) continue;
        const u = sx / sw;
        const v = sy / sw;

        if (u < 0 || u >= fw || v < 0 || v >= fh) continue;

        const ix = Math.floor(u);
        const iy = Math.floor(v);
        const fx = u - ix;
        const fy = v - iy;
        const ix1 = Math.min(ix + 1, fw - 1);
        const iy1 = Math.min(iy + 1, fh - 1);

        const idx00 = (iy * fw + ix) * 4;
        const idx10 = (iy * fw + ix1) * 4;
        const idx01 = (iy1 * fw + ix) * 4;
        const idx11 = (iy1 * fw + ix1) * 4;

        const r = (1 - fx) * (1 - fy) * fData[idx00] + fx * (1 - fy) * fData[idx10] + (1 - fx) * fy * fData[idx01] + fx * fy * fData[idx11];
        const gC = (1 - fx) * (1 - fy) * fData[idx00 + 1] + fx * (1 - fy) * fData[idx10 + 1] + (1 - fx) * fy * fData[idx01 + 1] + fx * fy * fData[idx11 + 1];
        const bC = (1 - fx) * (1 - fy) * fData[idx00 + 2] + fx * (1 - fy) * fData[idx10 + 2] + (1 - fx) * fy * fData[idx01 + 2] + fx * fy * fData[idx11 + 2];
        const aC = (1 - fx) * (1 - fy) * fData[idx00 + 3] + fx * (1 - fy) * fData[idx10 + 3] + (1 - fx) * fy * fData[idx01 + 3] + fx * fy * fData[idx11 + 3];

        const outIdx = (y * canvasW + x) * 4;
        const alpha = aC / 255;
        out[outIdx] = r * alpha + out[outIdx] * (1 - alpha);
        out[outIdx + 1] = gC * alpha + out[outIdx + 1] * (1 - alpha);
        out[outIdx + 2] = bC * alpha + out[outIdx + 2] * (1 - alpha);
        out[outIdx + 3] = 255;
      }
    }

    ctx.putImageData(outData, 0, 0);
  }

  if (failedFrames.length > 0) {
    console.error(
      `[buildFrameComposite] ${failedFrames.length}/${placedFrames.length} frame(s) failed to composite — ` +
      `they will be missing from the rendered canvas. See individual errors above.`,
      failedFrames
    );
  }

  return canvas.toDataURL('image/png', 1.0);
};

// ============================================
// INDEXEDDB CACHE — instant reopens within a session
// ============================================
const CACHE_DB = 'outlne-composite-cache-v1';
const CACHE_STORE = 'composites';
let cacheDbPromise = null;

const openCacheDb = () => {
  if (cacheDbPromise) return cacheDbPromise;
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  cacheDbPromise = new Promise((resolve) => {
    try {
      const req = indexedDB.open(CACHE_DB, 1);
      req.onupgradeneeded = () => {
        const dbRef = req.result;
        if (!dbRef.objectStoreNames.contains(CACHE_STORE)) {
          dbRef.createObjectStore(CACHE_STORE, { keyPath: 'key' });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return cacheDbPromise;
};

export const computeCompositeCacheKey = (workingBase, frames) => {
  const baseTail = typeof workingBase === 'string' ? workingBase.slice(-160) : 'x';
  const frameFp = (frames || [])
    .filter(f => f && f.corners)
    .map(f => `${f.id}:${JSON.stringify(f.corners)}:${f.naturalWidth}x${f.naturalHeight}`)
    .join('|');
  return `${baseTail}::${frameFp}`;
};

export const getCachedComposite = async (key) => {
  const dbRef = await openCacheDb();
  if (!dbRef) return null;
  return new Promise((resolve) => {
    try {
      const tx = dbRef.transaction(CACHE_STORE, 'readonly');
      const store = tx.objectStore(CACHE_STORE);
      const req = store.get(key);
      req.onsuccess = () => resolve(req.result?.dataUrl || null);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
};

export const setCachedComposite = async (key, dataUrl) => {
  const dbRef = await openCacheDb();
  if (!dbRef || !dataUrl) return;
  return new Promise((resolve) => {
    try {
      const tx = dbRef.transaction(CACHE_STORE, 'readwrite');
      const store = tx.objectStore(CACHE_STORE);
      store.put({ key, dataUrl, ts: Date.now() });
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
};

// ============================================
// PREFETCH — Dashboard entry point
// ----------------------------------------------------------------------------
// Loads the project doc, canvas state, frames subcollection, preloads every
// frame's artwork into the browser cache, rebuilds the working base from
// the pristine original + non-frame layers, then produces the composite.
//
// OnProgress signature: (percent: number, label: string) => void
//
// Returns: { projectData, payload }
//   projectData — the minimal project doc shape Dashboard/Editor expect
//   payload     — everything the Editor needs to render on first commit
// ============================================
export const prefetchProjectForEditor = async ({ authUser, projectId, onProgress }) => {
  const report = (pct, label) => {
    try { onProgress?.(Math.round(pct), label); } catch { /* noop */ }
  };

  if (!authUser || !projectId) {
    throw new Error('prefetchProjectForEditor: authUser and projectId required');
  }

  // ── 1. Project doc ────────────────────────────────────────────────
  report(3, 'Opening your project…');
  const projectRef = doc(db, 'projects', projectId);
  const projectSnap = await getDoc(projectRef);
  if (!projectSnap.exists()) throw new Error('Project not found');
  const project = { id: projectSnap.id, ...projectSnap.data() };

  // ── 2. Canvas state ───────────────────────────────────────────────
  report(12, 'Restoring your work…');
  let loadedLayers = [];
  let selectedLayerId = null;
  let currentLassoPath = null;

  if (project.canvasStateURL) {
    try {
      const res = await fetch(project.canvasStateURL);
      if (res.ok) {
        const parsed = await res.json();
        if (Array.isArray(parsed.layers)) loadedLayers = parsed.layers;
        if (parsed.selectedLayerId) selectedLayerId = parsed.selectedLayerId;
        if (parsed.currentLassoPath) currentLassoPath = parsed.currentLassoPath;
      }
    } catch (e) {
      console.warn('[prefetch] canvasStateURL fetch failed:', e);
    }
  } else if (project.canvasState) {
    try {
      const parsed = typeof project.canvasState === 'string'
        ? JSON.parse(project.canvasState)
        : project.canvasState;
      if (Array.isArray(parsed.layers)) loadedLayers = parsed.layers;
      if (parsed.selectedLayerId) selectedLayerId = parsed.selectedLayerId;
      if (parsed.currentLassoPath) currentLassoPath = parsed.currentLassoPath;
    } catch (e) {
      console.warn('[prefetch] legacy canvasState parse failed:', e);
    }
  }

  // ── 3. Frames ─────────────────────────────────────────────────────
  report(28, 'Loading your artwork…');
  let loadedFrames = null;

  try {
    const framesRef = collection(db, 'projects', projectId, 'frames');
    const framesSnap = await getDocs(framesRef);
    if (!framesSnap.empty) {
      const subFrames = framesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const legacyById = new Map(
        (Array.isArray(project.frameImages) ? project.frameImages : [])
          .filter(f => f && f.id)
          .map(f => [f.id, f])
      );
      loadedFrames = subFrames.map(sf => {
        const legacy = legacyById.get(sf.id) || {};
        return {
          ...legacy,
          ...sf,
          imageData: sf.imageData || sf.imageURL || legacy.imageData,
          originalImage: sf.originalImage || sf.originalURL || legacy.originalImage,
          originalUncroppedData: sf.originalUncroppedData || sf.uncroppedURL || legacy.originalUncroppedData,
          backgroundImage: sf.backgroundImage || sf.backgroundURL || legacy.backgroundImage,
        };
      });
    }
  } catch (e) {
    console.warn('[prefetch] frames subcollection read failed:', e);
  }

  if (!loadedFrames && project.frameImagesURL) {
    try {
      const res = await fetch(project.frameImagesURL);
      if (res.ok) loadedFrames = await res.json();
    } catch (e) {
      console.warn('[prefetch] frameImagesURL fetch failed:', e);
    }
  }

  if (!loadedFrames && Array.isArray(project.frameImages)) {
    loadedFrames = project.frameImages;
  }

  const frames = (loadedFrames || []).map(f => ({
    ...f,
    rotation: f.rotation || { x: 0, y: 0 },
  }));

  // ── 4. Preload frame bitmaps ──────────────────────────────────────
  if (frames.length > 0) {
    report(45, 'Preparing your images…');
    await Promise.all(
      frames.map(f => {
        const src = f.originalImage || f.imageData || f.dataUrl;
        if (!src) return Promise.resolve();
        return loadImageElement(src).catch(() => null);
      })
    );
  }

  // ── 5. Rebuild working base ───────────────────────────────────────
  report(62, 'Rebuilding your canvas…');
  const pristineBase = project.originalPhotoURL || project.photoURL || null;
  let workingBase = pristineBase;
  if (workingBase && loadedLayers.length > 0) {
    for (const layer of loadedLayers) {
      if (!layer) continue;
      if (layer.type === 'frame') continue;
      if (layer.visible === false) continue;
      if (layer.generatedImage) workingBase = layer.generatedImage;
    }
  }

  if (workingBase) {
    await loadImageElement(workingBase).catch(() => null);
  }

  // ── 6. Composite (cache-aware) ────────────────────────────────────
  report(78, 'Applying your frames…');
  const cacheKey = computeCompositeCacheKey(workingBase, frames);
  let composite = await getCachedComposite(cacheKey);
  if (!composite) {
    composite = await buildFrameComposite(workingBase, frames);
    if (composite) {
      // Fire-and-forget; the write should not block the open.
      setCachedComposite(cacheKey, composite).catch(() => { /* noop */ });
    }
  } else {
    console.log('[prefetch] composite cache HIT — skipped rebuild');
  }

  report(98, 'Almost ready…');

  const projectData = {
    id: projectId,
    projectId,
    name: project.name || 'Untitled',
    details: project.details || '',
    photoURL: workingBase || project.photoURL,
    originalPhotoURL: pristineBase,
    thumbnailURL: project.thumbnailURL,
    schemaVersion: project.schemaVersion || 4,
    mode: 'load-file',
  };

  const payload = {
    workingBase,
    pristineBase,
    layers: loadedLayers,
    frames,
    composite,
    selectedLayerId,
    currentLassoPath,
    schemaVersion: project.schemaVersion || 4,
  };

  report(100, 'Ready');
  return { projectData, payload };
};